/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { homeSections } from '@/packages/home/home-sections';

interface IaBuildContextProps {
  visibleSectionIds: number[];
  messageCompleted: boolean;
  isAllSectionsLoaded: boolean;
  showAllSections: boolean;
  nextSection: () => void;
  setMessageCompleted: (completed: boolean) => void;
  getMessageForSection: (sectionId: number) => string | null;
}

export const IaBuildContext = createContext<IaBuildContextProps | null>(null);

interface IaBuilderProviderProps {
  children: ReactNode;
  sections: ReturnType<typeof homeSections>;
  showAllSections: boolean;
}

export const IaBuilderProvider = ({
  children,
  sections,
  showAllSections,
}: IaBuilderProviderProps) => {
  const iaSections = sections.filter((s) => !s.skipIaBuilder);

  const [visibleSectionIds, setVisible] = useState<number[]>(() => {
    if (showAllSections) {
      return iaSections
        .map((section) => section.id)
        .filter((id): id is number => id !== undefined);
    }
    return iaSections.length > 0 && iaSections[0].id !== undefined
      ? [iaSections[0].id]
      : [];
  });
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (showAllSections) {
      return iaSections.length - 1;
    }
    return 0;
  });
  const [messageCompleted, setMessageCompleted] = useState(false);
  const [isAllSectionsLoaded, setIsAllSectionsLoaded] = useState(() => {
    return showAllSections;
  });

  const getMessageForSection = (sectionId: number) => {
    const section = iaSections.find((s) => s.id === sectionId);
    return section?.message ?? null;
  };

  const nextSection = () => {
    const nextIndex = currentIndex + 1;

    if (nextIndex < iaSections.length) {
      const next = iaSections[nextIndex];
      const nextId = next.id;
      if (nextId !== undefined) {
        setVisible((prev) => [...prev, nextId]);
      }
      setCurrentIndex(nextIndex);
      setMessageCompleted(false);
    } else {
      const skippedSections = sections
        .filter((s) => s.skipIaBuilder)
        .map((s) => s.id)
        .filter((id): id is number => id !== undefined);
      setVisible((prev) => [...prev, ...skippedSections]);
      setIsAllSectionsLoaded(true);
    }
  };

  const value = {
    visibleSectionIds,
    messageCompleted,
    isAllSectionsLoaded,
    showAllSections,
    nextSection,
    setMessageCompleted,
    getMessageForSection,
  };

  return (
    <IaBuildContext.Provider value={value}>{children}</IaBuildContext.Provider>
  );
};

export const useIaBuildContext = () => {
  const ctx = useContext(IaBuildContext);
  if (!ctx)
    throw new Error('useIaBuildContext must be used within IaBuilderProvider');
  return ctx;
};
